import {
  Badge,
  Box,
  Button,
  Center,
  Link as ChakraLink,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Col, Row } from "components/elements";
import { AppTitle } from "components/elements/AppTitle";
import { TextEnteringEffect } from "components/shared/TextEnteringEffect";
import React, { useState } from "react";
import {
  BsArrowDown,
  BsArrowRight,
  BsArrowUpRight,
  BsLockFill,
} from "react-icons/bs";
import useMetaTags from "react-metatags-hook";
import { colors } from "theme";

// Constants and typed data for Gen 8 Recruitment
import { RecruitmentCTAButton } from "../../components/RecruitmentCTAButton";
import { GEN8_CONFIG } from "./config";
import { BENEFITS, DEPARTMENTS, Department } from "./departments-data";

const Gen8RecruitmentPage: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDept, setSelectedDept] = useState<Department>(DEPARTMENTS[0]);

  useMetaTags({
    title: "GDGoC-DUT | Tuyển Thành Viên Gen 8",
    description:
      "Cơ hội trở thành mảnh ghép của Google Developer Groups on Campus - Danang University of Science and Technology (Gen 8). Khám phá 4 ban tuyển dụng: AI, Software, Marketing, Business Development và nộp đơn ngay!",
    charset: "utf8",
    lang: "vi",
    metas: [
      {
        name: "keywords",
        content: "GDGoC, GDSC, DUT, Gen 8, Recruitment, Tuyển thành viên",
      },
    ],
    links: [{ rel: "canonical", href: window.location.href }],
  });

  const handleOpenDept = (dept: Department) => {
    setSelectedDept(dept);
    onOpen();
  };

  return (
    <Box position="relative">
      {/* ─── SECTION 1: HERO SECTION ─── */}
      <Col
        justifyContent="flex-end"
        minH={{ base: "auto", "2xl": "calc(100vh - 80px)" }}
        pb={{ base: 12, md: 20 }}
      >
        <Col
          justifyContent="flex-end"
          pb={8}
          pl={{ base: "1rem", sm: "2rem", md: "4rem", "2xl": "9rem" }}
          pr={{ base: "1rem", sm: "2rem", md: "4rem" }}
          pt={{ base: "6rem", md: "8rem", "2xl": "9rem" }}
        >
          <TextEnteringEffect
            fontSize="xs"
            letterSpacing="0.2em"
            fontWeight="700"
            color={
              GEN8_CONFIG.status === "CLOSED" ? "google.red" : "google.green"
            }
            durationValue={0.8}
            delayValue={0.1}
          >
            <Text textTransform="uppercase" mb={3}>
              ● GDGoC DUT // GENERATION 8 CALL FOR MEMBERS{" "}
              {GEN8_CONFIG.status === "CLOSED"
                ? "— [CỔNG ỨNG TUYỂN ĐÃ ĐÓNG]"
                : ""}
            </Text>
          </TextEnteringEffect>

          <TextEnteringEffect
            fontSize={{
              base: "2.25rem",
              sm: "3.5rem",
              md: "4.5rem",
              "2xl": "6.5rem",
            }}
            lineHeight={{
              base: "2.5rem",
              sm: "3.8rem",
              md: "4.8rem",
              "2xl": "6.5rem",
            }}
            fontWeight="700"
            durationValue={1.2}
            delayValue={0.2}
          >
            <AppTitle>GEN 8 RECRUITMENT</AppTitle>
          </TextEnteringEffect>

          <TextEnteringEffect
            fontSize={{
              base: "2.25rem",
              sm: "3.5rem",
              md: "4.5rem",
              "2xl": "6.5rem",
            }}
            lineHeight={{
              base: "2.5rem",
              sm: "3.8rem",
              md: "4.8rem",
              "2xl": "6.5rem",
            }}
            fontWeight="700"
            durationValue={1.2}
            delayValue={0.3}
          >
            <AppTitle>
              {GEN8_CONFIG.status === "CLOSED"
                ? "ĐÃ CHÍNH THỨC KHÉP LẠI"
                : "BE A GOOGLER SPIRIT"}
            </AppTitle>
          </TextEnteringEffect>

          {GEN8_CONFIG.status === "CLOSED" && (
            <Flex wrap="wrap" alignItems="center" gap={3} mt={4}>
              <Badge
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="800"
                letterSpacing="0.08em"
                borderRadius="none"
                colorScheme="red"
                variant="solid"
                display="inline-flex"
                alignItems="center"
                gap={1.5}
              >
                <BsLockFill />
                CỔNG ỨNG TUYỂN ĐÃ ĐÓNG
              </Badge>
              <Text
                fontSize="xs"
                fontWeight="600"
                color="gray.600"
                letterSpacing="0.02em"
              >
                Đóng đơn lúc{" "}
                {GEN8_CONFIG.closedDateText || "21:59 ngày 27/09/2026"}
              </Text>
            </Flex>
          )}
        </Col>

        {/* Hero Architectural Statement Split */}
        <Row
          overflow="hidden"
          flexDirection={{ base: "column", xl: "row" }}
          backgroundColor={colors.mineShaft}
          mx={{ base: "0", md: "2rem", "2xl": "9rem" }}
          boxShadow="0 20px 40px -15px rgba(0,0,0,0.12)"
        >
          {/* Left Block: Green Brand Anchor */}
          <Col
            flex={1.2}
            backgroundColor="google.green"
            color="white"
            justifyContent="center"
            paddingX={{ base: "1.5rem", sm: "2.5rem", "2xl": "4rem" }}
            paddingY={{ base: "2.5rem", sm: "3rem", "2xl": "3.5rem" }}
            position="relative"
          >
            {GEN8_CONFIG.status === "CLOSED" ? (
              <>
                <TextEnteringEffect
                  fontSize="2xl"
                  fontWeight={700}
                  mb={3}
                  delayValue={0.35}
                >
                  Cảm ơn bạn đã đồng hành cùng Gen 8
                </TextEnteringEffect>

                <TextEnteringEffect
                  fontSize="md"
                  color="white"
                  delayValue={0.4}
                  my={2}
                  lineHeight="1.7"
                >
                  Cổng nộp đơn trực tuyến Gen 8 đã chính thức khép lại vào lúc{" "}
                  <strong>
                    {GEN8_CONFIG.closedDateText || "21:59 ngày 27/09/2026"}
                  </strong>
                  . GDGoC DUT xin gửi lời tri ân sâu sắc đến tất cả các bạn sinh
                  viên đã dành thời gian, tâm huyết và sự tin tưởng gửi gắm vào
                  kỳ tuyển chọn năm nay.
                </TextEnteringEffect>

                <Box
                  mt={4}
                  mb={2}
                  p={4}
                  bg="rgba(0, 0, 0, 0.15)"
                  borderLeft="4px solid white"
                >
                  <Text fontSize="sm" fontWeight="700" color="white" mb={1}>
                    ✦ Lưu ý quan trọng cho ứng viên:
                  </Text>
                  <Text fontSize="sm" color="whiteAlpha.900" lineHeight="1.6">
                    Kết quả Vòng Đơn và thông tin phỏng vấn sẽ được gửi trực
                    tiếp qua Email của từng ứng viên. Vui lòng kiểm tra thường
                    xuyên hộp thư đến (bao gồm cả thư mục Spam/Quảng cáo) để
                    không bỏ lỡ thông báo từ Ban Tuyển Dụng.
                  </Text>
                </Box>
              </>
            ) : (
              <>
                <TextEnteringEffect
                  fontSize="2xl"
                  fontWeight={700}
                  mb={3}
                  delayValue={0.35}
                >
                  By community, for community
                </TextEnteringEffect>

                <TextEnteringEffect
                  fontSize="md"
                  color="white"
                  delayValue={0.4}
                  my={2}
                >
                  Kỳ tuyển thành viên Thế hệ thứ 8 (Gen 8) của Google Developer
                  Groups on Campus - Danang University of Science and Technology
                  đã chính thức khởi động. Chúng mình tìm kiếm những người bạn
                  đồng hành dám nghĩ, dám làm, sẵn sàng học hỏi và cùng nhau
                  kiến tạo những giải pháp công nghệ giá trị cho cộng đồng.
                </TextEnteringEffect>
              </>
            )}

            <Flex mt={6} gap={4} wrap="wrap" alignItems="center">
              <RecruitmentCTAButton config={GEN8_CONFIG} variantType="hero" />

              <ChakraLink
                href="#departments"
                _hover={{ textDecoration: "none" }}
              >
                <Button
                  height="50px"
                  px={6}
                  borderRadius="0"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  fontWeight="600"
                  fontSize="sm"
                  letterSpacing="0.05em"
                  rightIcon={<BsArrowDown />}
                  _hover={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.2s"
                >
                  Khám phá 4 ban (JD)
                </Button>
              </ChakraLink>
            </Flex>
          </Col>

          {/* Right Block: Mineral Dark Editorial Note */}
          <Col
            flex={0.8}
            backgroundColor="#1c1b20"
            color="white"
            justifyContent="center"
            paddingX={{ base: "1.5rem", sm: "2.5rem", "2xl": "3rem" }}
            paddingY={{ base: "2.5rem", sm: "3rem" }}
            borderLeft={{
              base: "none",
              xl: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <Text
              fontSize="xs"
              letterSpacing="0.15em"
              color="gray.400"
              fontWeight="700"
              mb={2}
              textTransform="uppercase"
            >
              STATUS:{" "}
              {GEN8_CONFIG.status === "CLOSED"
                ? "APPLICATIONS CLOSED"
                : "OPEN FOR APPLICATIONS"}
            </Text>
            <Text fontSize="xl" fontWeight="600" mb={4} color="white">
              Cánh cửa dẫn vào hệ sinh thái Google Developers tại DUT
            </Text>
            <Text fontSize="sm" color="gray.300" lineHeight="1.7">
              Dù bạn đam mê kỹ thuật chuyên sâu (AI, Software) hay nhiệt huyết
              với sáng tạo và kết nối (Marketing, Business Development), GDGoC
              DUT luôn có một không gian thử thách xứng đáng để bạn bứt phá giới
              hạn.
            </Text>
          </Col>
        </Row>
      </Col>

      {/* ─── SECTION 2: WHY JOIN US (BENEFITS) ─── */}
      <Box
        paddingX={{ base: "1rem", sm: "2rem", md: "4rem", "2xl": "9rem" }}
        paddingY={{ base: "4rem", md: "6rem" }}
        backgroundColor={colors.alabaster}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor={colors.primaryText}
      >
        <Flex
          justifyContent="space-between"
          alignItems={{ base: "flex-start", lg: "flex-end" }}
          flexDirection={{ base: "column", lg: "row" }}
          mb={12}
          gap={6}
        >
          <Box>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.2em"
              color="google.green"
              textTransform="uppercase"
              mb={2}
            >
              {"// WHY GDGOC DUT?"}
            </Text>
            <AppTitle>QUYỀN LỢI & ĐẶC QUYỀN</AppTitle>
          </Box>
          <Text
            maxWidth="500px"
            fontSize="md"
            color={colors.primaryText}
            textAlign={{ base: "left", lg: "justify" }}
          >
            Không chỉ là một câu lạc bộ học thuật, GDGoC DUT là bệ phóng tài
            năng giúp bạn tích lũy kinh nghiệm thực chiến, hoàn thiện kỹ năng và
            kết nối với mạng lưới công nghệ quốc tế.
          </Text>
        </Flex>

        <Flex wrap="wrap" mx="-1rem">
          {BENEFITS.map((b) => (
            <Box
              key={b.number}
              width={{ base: "100%", md: "50%", lg: "25%" }}
              padding="1rem"
            >
              <Col
                height="100%"
                backgroundColor="white"
                padding="2rem"
                border="1px solid"
                borderColor={colors.primaryText}
                position="relative"
                transition="transform 0.25s cubic-bezier(1, 0, 0, 1)"
                _hover={{
                  transform: "translateY(-4px)",
                }}
              >
                <Text
                  fontSize="2.5rem"
                  fontWeight="700"
                  color="google.green"
                  lineHeight="1"
                  mb={4}
                  fontFamily="PoppinsExtraBold, GoogleSans, sans-serif"
                >
                  {b.number}
                </Text>
                <Text fontSize="lg" fontWeight="700" color="#1c1b20" mb={3}>
                  {b.title}
                </Text>
                <Text fontSize="sm" color="#525252" lineHeight="1.6">
                  {b.description}
                </Text>
              </Col>
            </Box>
          ))}
        </Flex>
      </Box>

      {/* ─── SECTION 3: DEPARTMENTS (THE 4 JDs) ─── */}
      <Box
        id="departments"
        paddingX={{ base: "1rem", sm: "2rem", md: "4rem", "2xl": "9rem" }}
        paddingY={{ base: "5rem", md: "7rem" }}
      >
        <Flex
          justifyContent="space-between"
          alignItems={{ base: "flex-start", lg: "flex-end" }}
          flexDirection={{ base: "column", lg: "row" }}
          mb={12}
          gap={6}
        >
          <Box>
            <AppTitle>4 BAN CHUYÊN MÔN</AppTitle>
          </Box>
          <Text
            maxWidth="500px"
            fontSize="md"
            color={colors.primaryText}
            textAlign={{ base: "left", lg: "justify" }}
          >
            Khám phá chi tiết bản mô tả công việc (Job Description) và tiêu chí
            của từng ban chuyên môn tại GDGoC DUT.
          </Text>
        </Flex>

        <Col width="100%">
          {DEPARTMENTS.map((dept) => (
            <Box
              key={dept.id}
              borderTop="1px solid"
              borderColor={colors.primaryText}
              paddingY={{ base: "2rem", md: "3rem" }}
              _last={{
                borderBottom: "1px solid",
                borderColor: colors.primaryText,
              }}
              transition="background-color 0.2s ease"
              _hover={{ backgroundColor: "rgba(15, 157, 88, 0.03)" }}
              cursor="pointer"
              onClick={() => handleOpenDept(dept)}
            >
              <Flex
                justifyContent="space-between"
                alignItems={{ base: "flex-start", md: "center" }}
                flexDirection={{ base: "column", md: "row" }}
                gap={4}
                mb={4}
              >
                <Box>
                  <Flex alignItems="center" gap={3} wrap="wrap">
                    <Text
                      fontSize={{
                        base: "1.75rem",
                        sm: "2.25rem",
                        md: "2.75rem",
                      }}
                      fontWeight="700"
                      color="#1c1b20"
                      letterSpacing="-0.01em"
                    >
                      {dept.name}
                    </Text>
                    {GEN8_CONFIG.status === "CLOSED" && (
                      <Badge
                        px={2.5}
                        py={1}
                        borderRadius="none"
                        colorScheme="gray"
                        fontSize="2xs"
                        fontWeight="800"
                        letterSpacing="0.05em"
                      >
                        ĐÃ ĐÓNG ĐƠN
                      </Badge>
                    )}
                  </Flex>
                </Box>

                {/* Circle Action Trigger */}
                <Center
                  width="54px"
                  height="54px"
                  borderRadius="50%"
                  border="1px solid"
                  borderColor={colors.primaryText}
                  backgroundColor="white"
                  color="#1c1b20"
                  transition="all 0.25s cubic-bezier(1, 0, 0, 1)"
                  _hover={{
                    backgroundColor: "google.green",
                    borderColor: "google.green",
                    color: "white",
                    transform: "translateX(6px)",
                  }}
                  title="Xem chi tiết bản mô tả công việc (Job Description)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDept(dept);
                  }}
                >
                  <BsArrowRight size={20} />
                </Center>
              </Flex>

              {/* Department Description & Focus tags */}
              <Row
                mt={{ base: 4, md: 6 }}
                gap={{ base: 4, xl: 8 }}
                flexDirection={{ base: "column", xl: "row" }}
                alignItems="flex-start"
              >
                <Box flex={1.2}>
                  <Text
                    fontSize="md"
                    color="#313131"
                    lineHeight="1.7"
                    textAlign="justify"
                  >
                    {dept.description}
                  </Text>
                  <Box mt={4}>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color="google.green"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      _hover={{ textDecoration: "underline" }}
                    >
                      Xem chi tiết bản mô tả công việc (Job Description){" "}
                      <BsArrowRight />
                    </Text>
                  </Box>
                </Box>

                <Box flex={0.8} width="100%">
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.1em"
                    color="gray.500"
                    mb={2}
                  >
                    CÔNG VIỆC & KỸ NĂNG TRỌNG TÂM:
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {dept.focusItems.map((item, i) => (
                      <Box
                        key={i}
                        border="1px solid"
                        borderColor="gray.300"
                        backgroundColor="white"
                        px={3}
                        py={1}
                        fontSize="xs"
                        fontWeight="500"
                        color="#313131"
                      >
                        {item}
                      </Box>
                    ))}
                  </Flex>
                </Box>
              </Row>
            </Box>
          ))}
        </Col>
      </Box>

      {/* ─── SECTION 4: FINAL CALL TO ACTION BANNER ─── */}
      <Box
        backgroundColor="google.green"
        color="white"
        paddingX={{ base: "1.5rem", sm: "2.5rem", md: "4rem", "2xl": "9rem" }}
        paddingY={{ base: "5rem", md: "7rem" }}
        position="relative"
        overflow="hidden"
      >
        <Col alignItems="center" textAlign="center" maxWidth="800px" mx="auto">
          <Text
            fontSize="xs"
            letterSpacing="0.25em"
            fontWeight="700"
            textTransform="uppercase"
            mb={4}
            opacity={0.9}
          >
            {"// JOIN OUR JOURNEY"}
          </Text>

          <Text
            fontSize={{ base: "2.5rem", sm: "3.5rem", md: "4.5rem" }}
            lineHeight={{ base: "2.8rem", sm: "3.8rem", md: "4.8rem" }}
            fontWeight="800"
            fontFamily="PoppinsExtraBold, GoogleSans, sans-serif"
            textTransform="uppercase"
            mb={6}
          >
            {GEN8_CONFIG.status === "CLOSED"
              ? "KỲ TUYỂN THÀNH VIÊN GEN 8 ĐÃ KHÉP LẠI"
              : "BẮT ĐẦU CHẶNG ĐƯỜNG MỚI CÙNG GEN 8"}
          </Text>

          <Text
            fontSize={{ base: "md", md: "lg" }}
            opacity={0.95}
            mb={10}
            lineHeight="1.8"
          >
            {GEN8_CONFIG.status === "CLOSED"
              ? "Cổng nộp đơn Gen 8 đã chính thức đóng để bắt đầu các vòng đánh giá. GDGoC - DUT xin chân thành cảm ơn sự quan tâm và nhiệt huyết của tất cả các bạn ứng viên. Hẹn gặp lại bạn tại các sự kiện và kỳ tuyển dụng tiếp theo!"
              : "Đừng bỏ lỡ cơ hội gia nhập cộng đồng sinh viên công nghệ năng động hàng đầu tại Đà Nẵng. Hãy hoàn thành đơn đăng ký của bạn ngay hôm nay!"}
          </Text>

          <RecruitmentCTAButton config={GEN8_CONFIG} variantType="section" />
        </Col>
      </Box>

      {/* ─── IN-PAGE JOB DESCRIPTION VIEWER (DRAWER) ─── */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
        <DrawerOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(3px)" />
        <DrawerContent
          borderRadius="0"
          backgroundColor="white"
          borderLeft="1px solid"
          borderColor={colors.primaryText}
          maxWidth={{ base: "100vw", lg: "850px" }}
          display="flex"
          flexDirection="column"
        >
          <DrawerCloseButton
            top={6}
            right={6}
            borderRadius="0"
            border="1px solid"
            borderColor="gray.300"
            _hover={{ backgroundColor: "gray.100" }}
          />

          {/* Drawer Header */}
          <DrawerHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            paddingX={{ base: 6, md: 10 }}
            paddingY={6}
            backgroundColor="#fafafa"
          >
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.15em"
              color="google.green"
              textTransform="uppercase"
              mb={1}
            >
              GDGoC DUT // JOB DESCRIPTION
            </Text>
            <Text
              fontSize={{ base: "1.75rem", md: "2.25rem" }}
              fontWeight="800"
              color="#1c1b20"
              fontFamily="PoppinsExtraBold, GoogleSans, sans-serif"
            >
              {selectedDept.name}
            </Text>
          </DrawerHeader>

          {/* Drawer Body */}
          <DrawerBody
            paddingX={{ base: 6, md: 10 }}
            paddingY={8}
            overflowY="auto"
          >
            <Col gap={8}>
              {/* 1. Giới thiệu chung */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  01 // GIỚI THIỆU CHUNG
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Về {selectedDept.name}
                </Text>
                {selectedDept.details.overview.map((p, i) => (
                  <Text
                    key={i}
                    fontSize="md"
                    color="#313131"
                    lineHeight="1.8"
                    mb={3}
                    textAlign="justify"
                  >
                    {p}
                  </Text>
                ))}
              </Box>

              {/* 2. Đối tượng phù hợp */}
              <Box borderTop="1px solid" borderColor="gray.200" pt={6}>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  02 // ĐỐI TƯỢNG PHÙ HỢP
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Chúng mình tìm kiếm ai?
                </Text>
                {selectedDept.details.targetAudience.lead && (
                  <Text fontSize="md" color="#555" fontStyle="italic" mb={3}>
                    {selectedDept.details.targetAudience.lead}
                  </Text>
                )}
                <Col gap={2}>
                  {selectedDept.details.targetAudience.items.map((item, i) => (
                    <Flex key={i} alignItems="flex-start" gap={3}>
                      <Text color="google.green" fontWeight="bold">
                        ●
                      </Text>
                      <Text fontSize="md" color="#313131" lineHeight="1.7">
                        {item}
                      </Text>
                    </Flex>
                  ))}
                </Col>
              </Box>

              {/* 3. Công việc của thành viên */}
              <Box borderTop="1px solid" borderColor="gray.200" pt={6}>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  03 // CÔNG VIỆC CỦA THÀNH VIÊN
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Bạn sẽ trực tiếp làm gì?
                </Text>
                {selectedDept.details.responsibilities.lead && (
                  <Text fontSize="md" color="#555" fontStyle="italic" mb={3}>
                    {selectedDept.details.responsibilities.lead}
                  </Text>
                )}
                <Col gap={2}>
                  {selectedDept.details.responsibilities.items.map(
                    (item, i) => (
                      <Flex key={i} alignItems="flex-start" gap={3}>
                        <Text color="google.green" fontWeight="bold">
                          ✔
                        </Text>
                        <Text fontSize="md" color="#313131" lineHeight="1.7">
                          {item}
                        </Text>
                      </Flex>
                    ),
                  )}
                </Col>
              </Box>

              {/* 4. Yêu cầu cơ bản & Điểm cộng lợi thế */}
              <Box borderTop="1px solid" borderColor="gray.200" pt={6}>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  04 // YÊU CẦU CƠ BẢN & LỢI THẾ
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Tiêu chí ứng viên
                </Text>
                {selectedDept.details.requirements.lead && (
                  <Text fontSize="md" color="#555" fontStyle="italic" mb={3}>
                    {selectedDept.details.requirements.lead}
                  </Text>
                )}
                <Col gap={2}>
                  {selectedDept.details.requirements.items.map((item, i) => (
                    <Flex key={i} alignItems="flex-start" gap={3}>
                      <Text color="google.green" fontWeight="bold">
                        ●
                      </Text>
                      <Text fontSize="md" color="#313131" lineHeight="1.7">
                        {item}
                      </Text>
                    </Flex>
                  ))}
                </Col>

                {/* Khối Điểm cộng lợi thế (nếu có) */}
                {selectedDept.details.requirements.advantages && (
                  <Box
                    mt={5}
                    p={5}
                    backgroundColor="#f9fbf9"
                    border="1px dashed"
                    borderColor="google.green"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="800"
                      letterSpacing="0.1em"
                      color="google.green"
                      textTransform="uppercase"
                      mb={3}
                    >
                      {selectedDept.details.requirements.advantages.lead ||
                        "Ứng viên sẽ có lợi thế nếu đáp ứng:"}
                    </Text>

                    {selectedDept.details.requirements.advantages.items && (
                      <Col gap={2}>
                        {selectedDept.details.requirements.advantages.items.map(
                          (adv, idx) => (
                            <Flex key={idx} alignItems="flex-start" gap={3}>
                              <Text color="google.green" fontWeight="bold">
                                ✦
                              </Text>
                              <Text
                                fontSize="md"
                                color="#313131"
                                lineHeight="1.7"
                              >
                                {adv}
                              </Text>
                            </Flex>
                          ),
                        )}
                      </Col>
                    )}

                    {selectedDept.details.requirements.advantages
                      .categories && (
                      <Col gap={4}>
                        {selectedDept.details.requirements.advantages.categories.map(
                          (cat, idx) => (
                            <Box key={idx}>
                              <Text
                                fontSize="sm"
                                fontWeight="800"
                                color="#1c1b20"
                                fontFamily="GoogleSans, sans-serif"
                                mb={1.5}
                              >
                                [ {cat.title} ]
                              </Text>
                              <Col gap={1.5} pl={2}>
                                {cat.items.map((item, itemIdx) => (
                                  <Flex
                                    key={itemIdx}
                                    alignItems="flex-start"
                                    gap={3}
                                  >
                                    <Text
                                      color="google.green"
                                      fontWeight="bold"
                                    >
                                      ✦
                                    </Text>
                                    <Text
                                      fontSize="md"
                                      color="#313131"
                                      lineHeight="1.7"
                                    >
                                      {item}
                                    </Text>
                                  </Flex>
                                ))}
                              </Col>
                            </Box>
                          ),
                        )}
                      </Col>
                    )}
                  </Box>
                )}
              </Box>

              {/* 5. Định hướng đánh giá đầu ra */}
              <Box borderTop="1px solid" borderColor="gray.200" pt={6}>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  05 // ĐỊNH HƯỚNG ĐÁNH GIÁ ĐẦU RA
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Mục tiêu & Đánh giá trong nhiệm kỳ
                </Text>
                {selectedDept.details.evaluation.lead && (
                  <Text fontSize="md" color="#555" fontStyle="italic" mb={3}>
                    {selectedDept.details.evaluation.lead}
                  </Text>
                )}
                <Col gap={4}>
                  {selectedDept.details.evaluation.groups.map((grp, idx) => (
                    <Box
                      key={idx}
                      p={4}
                      backgroundColor="#fcfcfc"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="800"
                        letterSpacing="0.08em"
                        color="google.green"
                        textTransform="uppercase"
                        mb={2.5}
                      >
                        ✦ {grp.title}
                      </Text>
                      <Col gap={2}>
                        {grp.items.map((item, itemIdx) => (
                          <Flex key={itemIdx} alignItems="flex-start" gap={3}>
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              ●
                            </Text>
                            <Text
                              fontSize="md"
                              color="#313131"
                              lineHeight="1.7"
                            >
                              {item}
                            </Text>
                          </Flex>
                        ))}
                      </Col>
                    </Box>
                  ))}
                </Col>
              </Box>

              {/* 6. Quyền lợi khi tham gia */}
              <Box
                backgroundColor="#f8f9fa"
                p={6}
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  letterSpacing="0.1em"
                  color="google.green"
                  textTransform="uppercase"
                  mb={2}
                >
                  06 // QUYỀN LỢI ĐẶC QUYỀN
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="700"
                  color="#1c1b20"
                  mb={3}
                  fontFamily="GoogleSans, sans-serif"
                >
                  Bạn nhận được gì từ GDGoC DUT?
                </Text>
                <Col gap={2}>
                  {selectedDept.details.benefits.items.map((item, i) => (
                    <Flex key={i} alignItems="flex-start" gap={3}>
                      <Text color="google.green" fontWeight="bold">
                        ★
                      </Text>
                      <Text fontSize="md" color="#313131" lineHeight="1.7">
                        {item}
                      </Text>
                    </Flex>
                  ))}
                </Col>
              </Box>

              {/* 7. Lời kết và chào mừng */}
              {selectedDept.details.closingNote && (
                <Box
                  p={6}
                  backgroundColor="#f4f9f4"
                  borderLeft="4px solid"
                  borderColor="google.green"
                >
                  <Text
                    fontSize="md"
                    color="#1c1b20"
                    fontStyle="italic"
                    lineHeight="1.8"
                    mb={2}
                  >
                    "{selectedDept.details.closingNote}"
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.1em"
                    color="google.green"
                    textTransform="uppercase"
                  >
                    — Ban Tổ Chức Tuyển Thành Viên Gen 8 // GDGoC DUT
                  </Text>
                </Box>
              )}
            </Col>
          </DrawerBody>

          {/* Drawer Sticky Footer */}
          <Box
            borderTop="1px solid"
            borderColor="gray.200"
            paddingX={{ base: 6, md: 10 }}
            paddingY={4}
            backgroundColor="white"
            boxShadow="0 -4px 15px rgba(0,0,0,0.05)"
          >
            <Flex
              justifyContent={{ base: "stretch", sm: "flex-end" }}
              alignItems="center"
              gap={4}
            >
              <RecruitmentCTAButton
                config={GEN8_CONFIG}
                variantType="drawer"
                departmentName={selectedDept.name}
                width={{ base: "100%", sm: "auto" }}
                height={{ base: "auto", sm: "50px" }}
                minHeight="50px"
                px={{ base: 4, sm: 8 }}
                py={{ base: 3, sm: 0 }}
                borderRadius="0"
                backgroundColor="google.green"
                color="white"
                fontWeight="700"
                fontSize={{ base: "xs", sm: "sm" }}
                letterSpacing={{ base: "0.04em", sm: "0.08em" }}
                lineHeight="1.4"
                textAlign="center"
                whiteSpace="normal"
                textTransform="uppercase"
                rightIcon={<BsArrowUpRight />}
                _hover={{
                  backgroundColor: "#0d884c",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Gen8RecruitmentPage;
